import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { BASE_PATH, getAccessToken, getData, postData } from "../api/api";
import { REACT_APP_WS_SOCKJS_URL } from "../config/runtimeConfig";
import { embedRequestHeaders } from "./embedRuntime";

const BASE = "/warehouse-query-jobs";
const WS_SOCKJS = REACT_APP_WS_SOCKJS_URL || `${BASE_PATH.replace(/\/$/, "")}/ws-sockjs`;

export type WarehouseQueryJobStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED" | "CANCELLED";

/** Backend `WarehouseQueryJobService.view`; rows/columns only once SUCCEEDED. */
export interface WarehouseQueryJobView {
  jobId: string;
  status: WarehouseQueryJobStatus;
  adaptorId?: string;
  adaptorName?: string;
  engine?: string;
  sql?: string;
  engineQueryId?: string;
  progressPercent?: number;
  processedRows?: number;
  processedBytes?: number;
  queuedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  error?: string;
  columns?: string[];
  rows?: Record<string, unknown>[];
  rowCount?: number;
  truncated?: boolean;
  /** Pre-run engine estimate (EXPLAIN / dry run). */
  estimatedRows?: number;
  estimatedBytes?: number;
  /** Set when the shared result cache answered. */
  cachedAt?: string;
  /** Position among this adaptor's waiting AI queries while QUEUED. */
  queuePosition?: number;
  /** Cancel requested; the pod running the job stops it shortly. */
  cancelRequested?: boolean;
  exportStatus?: "RUNNING" | "SUCCEEDED" | "FAILED";
  exportRowCount?: number;
  exportError?: string;
}

export function isTerminalJobStatus(status: WarehouseQueryJobStatus | undefined): boolean {
  return status === "SUCCEEDED" || status === "FAILED" || status === "CANCELLED";
}

export const warehouseQueryJobService = {
  get: (jobId: string) => getData(`${BASE}/${jobId}`) as Promise<WarehouseQueryJobView>,

  cancel: (jobId: string) => postData(`${BASE}/${jobId}/cancel`, {}) as Promise<WarehouseQueryJobView>,

  /** Re-runs the query without the chat row cap into a CSV.gz in object storage. */
  startExport: (jobId: string) => postData(`${BASE}/${jobId}/export`, {}) as Promise<WarehouseQueryJobView>,

  /** Downloads the finished export through the authenticated API (blob → file save). */
  downloadExport: async (jobId: string): Promise<void> => {
    const token = await getAccessToken();
    const response = await fetch(`${BASE_PATH.replace(/\/$/, "")}${BASE}/${jobId}/export/download`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...embedRequestHeaders() },
    });
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `query-${jobId}.csv.gz`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Live job updates from the chat conversation topic (`type: "warehouse-job"` frames). Stops by itself once the
   * job reaches a terminal state.
   */
  subscribe: async (
    correlationId: string,
    jobId: string,
    onUpdate: (job: WarehouseQueryJobView) => void
  ): Promise<() => void> => {
    const token = await getAccessToken();
    if (!token) return () => {};
    const client = new Client({
      webSocketFactory: () =>
        new SockJS(`${WS_SOCKJS}?access_token=${encodeURIComponent(token)}`) as any,
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(`/topic/orchestration-response/${correlationId}`, (message) => {
          try {
            const frame = JSON.parse(message.body) as { type?: string; result?: unknown };
            if (frame.type !== "warehouse-job" || !frame.result || typeof frame.result !== "object") return;
            const job = frame.result as WarehouseQueryJobView;
            if (job.jobId !== jobId) return;
            onUpdate(job);
            if (isTerminalJobStatus(job.status)) {
              void client.deactivate();
            }
          } catch {
            // ignore malformed frames
          }
        });
      },
    });
    client.activate();
    return () => {
      void client.deactivate();
    };
  },
};
